
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Award, BookOpen, Users, Globe } from 'lucide-react';

const JoseLuisMarin = () => {
  const { t, getLocalizedPath } = useLanguage();

  return (
    <>
      <Helmet>
        <title>Dr. José Luis Marín - Founder & Chief Trainer | Global Therapy Hub</title>
        <meta name="description" content="Learn about Dr. José Luis Marín, founder of Global Therapy Hub and developer of Brief Psychotherapy training program." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="container mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  Dr. José Luis Marín López
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Founder, Chief Trainer & Visionary behind Global Therapy Hub
                </p>
                <p className="text-lg text-gray-700 mb-8">
                  With over 40 years of experience as a psychiatrist, psychotherapist, and trainer, 
                  Dr. Marín has revolutionized mental health care accessibility worldwide.
                </p>
                <Button size="lg" asChild>
                  <Link to="https://formacionpsicoterapia.com" target="_blank">
                    Visit Training Program
                  </Link>
                </Button>
              </div>
              <div className="text-center">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <div className="w-48 h-48 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                    <span className="text-white text-6xl font-bold">JM</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Dr. José Luis Marín</h3>
                  <p className="text-gray-600">Psychiatrist & Psychotherapist</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Professional Background */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Professional Background</h2>
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Academic Excellence</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Degree in Medicine from Complutense University of Madrid</li>
                    <li>• Specialization in Psychiatry at the University of Ghent, Belgium</li>
                    <li>• Studies in Psychosomatic Medicine at Ospedale Civile de Vicenza, Italy</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Leadership Positions</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Founder and former President of the International Forum for Psychotherapy Training</li>
                    <li>• President of the Spanish Society of Psychosomatic Medicine and Psychotherapy</li>
                    <li>• Honorary President of FAPyMPE (Federation of Associations of Psychologists and Medical Psychotherapists of Spain)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Achievements */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Achievements & Impact</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <Users className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-3xl font-bold text-gray-900 mb-2">30,000+</h3>
                <p className="text-gray-600">Mental health professionals trained</p>
              </div>
              <div className="text-center">
                <BookOpen className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-3xl font-bold text-gray-900 mb-2">40+</h3>
                <p className="text-gray-600">Years of clinical experience</p>
              </div>
              <div className="text-center">
                <Award className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Pioneer</h3>
                <p className="text-gray-600">In Brief Psychotherapy development</p>
              </div>
              <div className="text-center">
                <Globe className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Global</h3>
                <p className="text-gray-600">International recognition</p>
              </div>
            </div>
          </div>
        </section>

        {/* Vision */}
        <section className="py-16 px-4">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold mb-8">Dr. Marín's Vision</h2>
            <blockquote className="text-2xl text-gray-700 italic max-w-4xl mx-auto mb-8">
              "My goal is to democratize access to mental health, integrating quality training for 
              professionals and accessible solutions for patients in a connected world. This directory 
              is a step towards global, efficient and humane psychological care."
            </blockquote>
            <p className="text-lg text-gray-600">- Dr. José Luis Marín López</p>
          </div>
        </section>
      </div>
    </>
  );
};

export default JoseLuisMarin;
